import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { 
  UserCheck, CheckCircle, XCircle, Loader2, 
  QrCode, Wifi, WifiOff, Clock, Users, Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import PhygitalIcon from "@/components/PhygitalIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckInKioskPage() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanInput, setScanInput] = useState("");
  const [processing, setProcessing] = useState(false);
  const [lastCheckIn, setLastCheckIn] = useState(null);
  const [checkInStatus, setCheckInStatus] = useState(null); // 'success', 'error', 'already'
  const [recentCheckIns, setRecentCheckIns] = useState([]);
  const [stats, setStats] = useState({ total: 0, checkedIn: 0 });
  const inputRef = useRef(null);
  
  // Auto-focus the input for NFC reader keyboard emulation
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Keep focus on input (for continuous scanning)
    const keepFocus = setInterval(() => {
      if (inputRef.current && document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
    }, 1000);
    
    return () => clearInterval(keepFocus);
  }, []);

  // Fetch event data
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await fetch(`${API}/events/${eventId}`);
        if (!res.ok) throw new Error("Event not found");
        const data = await res.json();
        setEvent(data);
        
        // Fetch check-in stats
        fetchStats();
        fetchRecentCheckIns();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (eventId) fetchEvent();
  }, [eventId]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/events/${eventId}/checkin/stats`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const fetchRecentCheckIns = async () => {
    try {
      const res = await fetch(`${API}/events/${eventId}/checkin/recent?limit=10`);
      if (res.ok) {
        const data = await res.json();
        setRecentCheckIns(data);
      }
    } catch (err) {
      console.error("Failed to fetch recent check-ins:", err);
    }
  };

  // Handle NFC scan input
  const handleScan = async (inputValue) => {
    if (!inputValue.trim() || processing) return;
    
    setProcessing(true);
    setCheckInStatus(null);
    
    try {
      // Parse the NFC data - could be JSON or serial number
      let rsvpId = null;
      let scanData = inputValue.trim();
      
      // Try parsing as JSON (NFC payload)
      try {
        const parsed = JSON.parse(scanData);
        if (parsed.rsvp_id) {
          rsvpId = parsed.rsvp_id;
        }
      } catch {
        // Not JSON - check if it's a serial number format: phygital-{event_id}-{rsvp_id}
        if (scanData.startsWith("phygital-")) {
          const parts = scanData.split("-");
          if (parts.length >= 3) {
            rsvpId = parts.slice(2).join("-"); // Get rsvp_id part
          }
        } else if (scanData.startsWith("rsvp_")) {
          // Direct RSVP ID
          rsvpId = scanData;
        }
      }
      
      if (!rsvpId) {
        throw new Error("Invalid pass data");
      }
      
      // Call check-in API
      const res = await fetch(`${API}/events/${eventId}/checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rsvp_id: rsvpId })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (data.already_checked_in) {
          setCheckInStatus("already");
          setLastCheckIn(data.guest);
          toast.info(`${data.guest?.name || "Guest"} already checked in`);
        } else {
          setCheckInStatus("success");
          setLastCheckIn(data.guest);
          toast.success(`Welcome, ${data.guest?.name || "Guest"}!`);
          
          // Update stats and recent list
          fetchStats();
          fetchRecentCheckIns();
        }
      } else {
        throw new Error(data.detail || "Check-in failed");
      }
    } catch (err) {
      setCheckInStatus("error");
      setLastCheckIn({ error: err.message });
      toast.error(err.message);
    } finally {
      setProcessing(false);
      setScanInput("");
      
      // Clear status after 3 seconds
      setTimeout(() => {
        setCheckInStatus(null);
        setLastCheckIn(null);
      }, 4000);
    }
  };

  // Handle input change - auto-submit on complete scan (NFC reader sends Enter)
  const handleInputChange = (e) => {
    const value = e.target.value;
    setScanInput(value);
  };

  // Handle key press - NFC readers typically send Enter after data
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && scanInput.trim()) {
      e.preventDefault();
      handleScan(scanInput);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading check-in kiosk...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-white text-xl mb-2">Error</p>
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900" data-testid="checkin-kiosk">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PhygitalIcon className="w-8 h-8" variant="dark" />
            <div>
              <h1 className="text-white font-semibold">{event?.name || "Event"}</h1>
              <p className="text-white/50 text-sm">Check-In Kiosk</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="border-green-500/30 bg-green-500/10 text-green-400">
              <Wifi className="w-3 h-3 mr-1" />
              NFC Ready
            </Badge>
            <div className="text-right">
              <p className="text-2xl font-bold text-white">{stats.checkedIn}</p>
              <p className="text-white/50 text-xs">of {stats.total} checked in</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Scan Area */}
      <main className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-2xl w-full">
          {/* Status Display */}
          <div className="min-h-[300px] flex items-center justify-center mb-8">
            {checkInStatus === "success" && lastCheckIn && (
              <div className="text-center animate-in fade-in zoom-in duration-300">
                <div className="w-32 h-32 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-20 h-20 text-green-400" />
                </div>
                <h2 className="text-4xl font-bold text-white mb-2">Welcome!</h2>
                <p className="text-2xl text-green-400">{lastCheckIn.name}</p>
                {lastCheckIn.label && (
                  <Badge className="mt-4 bg-white/10 text-white">
                    {lastCheckIn.label}
                  </Badge>
                )}
              </div>
            )}
            
            {checkInStatus === "already" && lastCheckIn && (
              <div className="text-center animate-in fade-in zoom-in duration-300">
                <div className="w-32 h-32 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                  <UserCheck className="w-20 h-20 text-amber-400" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">Already Checked In</h2>
                <p className="text-xl text-amber-400">{lastCheckIn.name}</p>
                <p className="text-white/50 mt-2">Checked in at {lastCheckIn.checked_in_at ? new Date(lastCheckIn.checked_in_at).toLocaleTimeString() : 'earlier'}</p>
              </div>
            )}
            
            {checkInStatus === "error" && (
              <div className="text-center animate-in fade-in zoom-in duration-300">
                <div className="w-32 h-32 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-20 h-20 text-red-400" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">Check-In Failed</h2>
                <p className="text-red-400">{lastCheckIn?.error || "Please try again"}</p>
              </div>
            )}
            
            {!checkInStatus && !processing && (
              <div className="text-center">
                <div className="w-40 h-40 rounded-full bg-white/5 border-2 border-dashed border-white/20 flex items-center justify-center mx-auto mb-6 animate-pulse">
                  <QrCode className="w-20 h-20 text-white/30" />
                </div>
                <h2 className="text-3xl font-light text-white mb-2">Tap Your Pass</h2>
                <p className="text-white/50">Hold your Apple Wallet pass near the reader</p>
              </div>
            )}
            
            {processing && (
              <div className="text-center animate-in fade-in duration-200">
                <div className="w-32 h-32 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-20 h-20 text-purple-400 animate-spin" />
                </div>
                <h2 className="text-2xl font-light text-white">Processing...</h2>
              </div>
            )}
          </div>

          {/* Hidden input for NFC reader */}
          <div className="relative">
            <Input
              ref={inputRef}
              type="text"
              value={scanInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="NFC scan data will appear here..."
              className="w-full h-16 text-lg bg-white/5 border-white/10 text-white placeholder:text-white/30 text-center font-mono"
              autoFocus
              data-testid="nfc-scan-input"
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <Search className="w-5 h-5 text-white/30" />
            </div>
          </div>
          
          <p className="text-center text-white/40 text-sm mt-3">
            Or manually enter RSVP ID and press Enter
          </p>
        </div>
      </main>

      {/* Recent Check-ins Sidebar */}
      <aside className="fixed right-0 top-20 bottom-0 w-80 bg-black/50 backdrop-blur-md border-l border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-white/50" />
            <span className="text-white/50 text-sm font-medium">Recent Check-ins</span>
          </div>
        </div>
        <div className="overflow-y-auto h-full pb-20">
          {recentCheckIns.length === 0 ? (
            <div className="p-4 text-center text-white/30">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No check-ins yet</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {recentCheckIns.map((guest, idx) => (
                <div 
                  key={guest.rsvp_id || idx} 
                  className="p-4 flex items-center gap-3 hover:bg-white/5 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-medium">
                    {(guest.name || "G")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{guest.name}</p>
                    <p className="text-white/40 text-xs">
                      {guest.checked_in_at ? new Date(guest.checked_in_at).toLocaleTimeString() : "Just now"}
                    </p>
                  </div>
                  {guest.label && (
                    <Badge variant="outline" className="border-white/20 text-white/60 text-xs">
                      {guest.label}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
